import { baseURL } from "../utils/constants";
import axiosInstance from "../utils/axiosUtil";

export interface Profile {
  id: string; // UUID - primary identifier
  hospital_id: string; // UUID
  tv_profile_name: string; // Profile name (1-255 chars)
  view_type: "column" | "carousel" | "single-doctor" | null;
  language: "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr" | null;
  token: boolean; // Display token number
  patient_name: boolean; // Display patient name
  room_number: boolean; // Display room number
  slot: boolean; // Display slot time
  booking_mode: boolean; // Display booking mode
  is_active: boolean; // Whether profile is active
  is_default: boolean; // Whether this is a default profile (read-only)
  doctors_list?: Array<{
    id: string; // Doctor UUID
    name: string; // Doctor name
  }>;
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
  // Legacy fields for backward compatibility
  tv_config_profile_id?: string;
  profile_id?: string;
  profile_name?: string;
  doctor_id?: string;
  doctor_name?: string;
}

export interface CreateProfile {
  hospital_id: string; // UUID - required
  tv_profile_name: string; // Profile name (1-255 characters) - required
  view_type: "column" | "carousel" | "single-doctor"; // required
  language?: "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr"; // optional
  token?: boolean; // optional
  patient_name?: boolean; // optional
  room_number?: boolean; // optional
  slot?: boolean; // optional
  booking_mode?: boolean; // optional
  is_active?: boolean; // optional
  doctors_list?: string[]; // Array of doctor UUIDs - optional
}

export interface UpdateProfile {
  tv_profile_name?: string; // Not editable for default profiles
  view_type?: "column" | "carousel" | "single-doctor" | null; // Not editable for default profiles
  language?: "en" | "kn" | "ta" | "hi" | "ar" | "te" | "mr" | null;
  token?: boolean;
  patient_name?: boolean;
  room_number?: boolean;
  slot?: boolean;
  booking_mode?: boolean;
  is_active?: boolean; // Not editable for default profiles
  doctors_list?: string[]; // Array of doctor UUIDs - Not editable for default profiles
}

// GET all profiles by hospital ID (TV Configuration Profiles)
export const getProfilesByHospitalId = async (hospitalId: string) => {
  try {
    const res = await axiosInstance.get(`${baseURL}/tv-configuration/profiles/${hospitalId}`);
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

// GET profile by profile ID
export const getProfileById = async (profileId: string) => {
  try {
    const res = await axiosInstance.get(`${baseURL}/tv-configuration/profile/${profileId}`);
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

// GET profile by hospital ID and profile ID
export const getProfileByHospitalAndProfileId = async (hospitalId: string, profileId: string) => {
  try {
    const res = await axiosInstance.get(`${baseURL}/tv-configuration/profile/${profileId}`);
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

// CREATE new profile
export const createProfile = async (profileData: CreateProfile) => {
  try {
    const res = await axiosInstance.post(`${baseURL}/tv-configuration/profile`, profileData);
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

// UPDATE profile (PATCH method)
export const updateProfile = async (profileId: string, profileData: UpdateProfile) => {
  try {
    const res = await axiosInstance.patch(`${baseURL}/tv-configuration/profile/${profileId}`, profileData);
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

// UPDATE profile by hospital ID and profile ID (PATCH method)
export const updateProfileByHospitalAndProfileId = async (
  hospitalId: string,
  profileId: string,
  profileData: UpdateProfile
) => {
  try {
    const res = await axiosInstance.patch(
      `${baseURL}/tv-configuration/profile/${profileId}`,
      profileData
    );
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

// DELETE profile
export const deleteProfile = async (profileId: string) => {
  try {
    const res = await axiosInstance.delete(`${baseURL}/tv-configuration/profile/${profileId}`);
    return res;
  } catch (error: any) {
    if (error.response || error.response.status === 403) {
      return error.response;
    } else {
      console.error(error);
    }
  }
};

